import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";


const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface DicRow {
  dic_file_name: string;
  short_desc: string;
  subject: string;
  country: string;
  avl_in_aus: boolean;
  avl_in_can: boolean;
  remarks: string;
}

interface LsnRow {
  l_or_t: string;
  child_code: string;
  child_short_desc: string;
  dic_file_name: string;
  remarks: string;
}

interface ValidateLesson {
  child_code: string;
  description: string;
  level_no: number;
  exists: boolean;
  remarks: string;
}

interface ValidateDicResult {
  dic_file_name: string;
  country: string;
  mode: string;
  lessons: ValidateLesson[];
  tests: ValidateLesson[];
  missing_deps: { parent_code: string; dep_code: string; remarks: string }[];
  error_mesg: string;
}
const EL_CheckData: React.FC = () => {
  const [dicRows, setDicRows] = useState<DicRow[]>([]);
  const [lsnRows, setLsnRows] = useState<LsnRow[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const [dicSelIdx, setDicSelIdx] = useState<number>(-1);
  const [lsnSelIdx, setLsnSelIdx] = useState<number>(-1);
  const [dicSortCol, setDicSortCol] = useState<number>(-1);
  const [dicSortAsc, setDicSortAsc] = useState(true);
  const [lsnSortCol, setLsnSortCol] = useState<number>(-1);
  const [lsnSortAsc, setLsnSortAsc] = useState(true);

  const [validateOpen, setValidateOpen] = useState(false);
  const [validateResult, setValidateResult] = useState<ValidateDicResult | null>(null);
  const [validateLoading, setValidateLoading] = useState(false);

  const [alertMsg, setAlertMsg] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);

  const dicDivRef = useRef<HTMLDivElement>(null);
  const lsnDivRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  document.body.style.backgroundColor = "#ffffff";

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/check-data/dic-list/`);
        const data = await res.json();
        setDicRows(data.dic_rows || []);
        setLsnRows(data.lesson_rows || []);
        if (data.error_mesg) {
          setErrorMsg(data.error_mesg);
          setAlertOpen(true);
          setAlertMsg(data.error_mesg);
        }
      } catch (e) {
        setErrorMsg(String(e));
        setAlertOpen(true);
        setAlertMsg(String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setSize = useCallback(() => {
    const h = window.innerHeight - 100;
    const w = window.innerWidth - 30;
    if (dicDivRef.current) {
      dicDivRef.current.style.height = `${h}px`;
      dicDivRef.current.style.width = `${w}px`;
    }
    if (lsnDivRef.current) {
      lsnDivRef.current.style.height = `${h}px`;
      lsnDivRef.current.style.width = `${w}px`;
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(setSize, 300);
    window.addEventListener("resize", setSize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", setSize);
    };
  }, [setSize]);

  const dicHeaders = [
    "#", "DicFileName", "Dic Description", "Subject", "Country",
    "Australia", "Canada", "Remarks",
  ];
  const lsnHeaders = [
    "Lesson/Test", "LessonCode", "Description", "Dic File(s)", "Remarks",
  ];

  const dicKey = (col: number): keyof DicRow => {
    const keys: (keyof DicRow)[] = [
      "dic_file_name", "short_desc", "subject", "country",
      "avl_in_aus", "avl_in_can", "remarks",
    ];
    return keys[col];
  };
  const lsnKey = (col: number): keyof LsnRow => {
    const keys: (keyof LsnRow)[] = [
      "l_or_t", "child_code", "child_short_desc", "dic_file_name", "remarks",
    ];
    return keys[col];
  };

  const sortedDicRows = useMemo(() => {
    if (dicSortCol < 0) return dicRows;
    const key = dicKey(dicSortCol);
    return [...dicRows].sort((a, b) => {
      const va = String(a[key] ?? "").toLowerCase();
      const vb = String(b[key] ?? "").toLowerCase();
      return dicSortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [dicRows, dicSortCol, dicSortAsc]);

  const sortedLsnRows = useMemo(() => {
    if (lsnSortCol < 0) return lsnRows;
    const key = lsnKey(lsnSortCol);
    return [...lsnRows].sort((a, b) => {
      const va = String(a[key] ?? "").toLowerCase();
      const vb = String(b[key] ?? "").toLowerCase();
      return lsnSortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [lsnRows, lsnSortCol, lsnSortAsc]);

  const fn_ValidateDIC = useCallback(
    async (mode: "C" | "D") => {
      if (dicSelIdx < 0) {
        setAlertMsg("Please select a DIC");
        setAlertOpen(true);
        return;
      }
      const selected = sortedDicRows[dicSelIdx];
      if (!selected) return;

      const dicFileName = selected.dic_file_name;
      const country = selected.country;

      setValidateLoading(true);
      setValidateOpen(true);
      setValidateResult(null);

      try {
        let url = `${API_BASE_URL}api/check-data/validate-dic/?DicFileName=${encodeURIComponent(dicFileName)}&mode=${mode}`;
        if (mode === "C") url += `&Country=${encodeURIComponent(country)}`;

        const res = await fetch(url);
        const data = await res.json() as ValidateDicResult;
        setValidateResult(data);
      } catch (e) {
        setValidateResult({
          dic_file_name: dicFileName,
          country, mode,
          lessons: [], tests: [], missing_deps: [],
          error_mesg: String(e),
        });
      } finally {
        setValidateLoading(false);
      }
    },
    [dicSelIdx, sortedDicRows]
  );

  const exportTSV = (rows: unknown[], headers: string[]) => {
    const lines = [headers.join("\t")];
    for (const r of rows as Record<string, unknown>[]) {
      lines.push(headers.map((_, i) => {
        const keys = Object.values(r);
        return String(keys[i] ?? "");
      }).join("\t"));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/tab-separated-values" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "export.tsv";
    a.click();
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: "Tahoma, Arial" }}>
        Loading DIC validation data...
      </div>
    );
  }

  return (
    <>

      {alertOpen && (
        <>
          <div style={overlayStyle} />
          <div style={alertBoxStyle}>
            <div style={{ background: "#CC0000", color: "#fff", padding: "6px 12px", fontWeight: "bold" }}>
              ✕ Error
            </div>
            <div style={{ padding: "20px 24px", textAlign: "center" }}
              dangerouslySetInnerHTML={{ __html: alertMsg }}
            />
            <div style={{ textAlign: "center", paddingBottom: 14 }}>
              <button style={okBtnStyle} onClick={() => setAlertOpen(false)}>OK</button>
            </div>
          </div>
        </>
      )}

      {validateOpen && (
        <>
          <div style={overlayStyle} />
          <div style={validateModalStyle}>
            {/* Title bar */}
            <div style={modalTitleStyle}>
              DIC Validation – {validateResult?.dic_file_name || "…"}
              &nbsp;
              {validateResult?.mode === "C" ? "(Common Folder)" : "(DIC Folder)"}
              <button
                onClick={() => setValidateOpen(false)}
                style={{ float: "right", cursor: "pointer", border: "none", background: "transparent", color: "#fff", fontSize: 16 }}
              >✕</button>
            </div>

            {/* Body */}
            <div style={{ overflow: "auto", flex: 1, padding: 12 }}>
              {validateLoading && (
                <div style={{ textAlign: "center", padding: 40 }}>Validating…</div>
              )}
              {validateResult && !validateLoading && (
                <>
                  {validateResult.error_mesg && (
                    <p style={{ color: "red" }}>{validateResult.error_mesg}</p>
                  )}

                  {/* Lessons */}
                  <h3 style={sectionTitleStyle}>Lessons ({validateResult.lessons.length})</h3>
                  <ValidateGrid rows={validateResult.lessons} />

                  {/* Tests */}
                  <h3 style={{ ...sectionTitleStyle, marginTop: 16 }}>Tests ({validateResult.tests.length})</h3>
                  <ValidateGrid rows={validateResult.tests} />

                  {/* Missing deps */}
                  {validateResult.missing_deps.length > 0 && (
                    <>
                      <h3 style={{ ...sectionTitleStyle, marginTop: 16, color: "red" }}>
                        Missing Dependencies ({validateResult.missing_deps.length})
                      </h3>
                      <table style={tableStyle}>
                        <thead>
                          <tr>
                            {["Parent Code", "Dep Code", "Remarks"].map((h) => (
                              <th key={h} style={thStyle}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {validateResult.missing_deps.map((d, i) => (
                            <tr key={i} style={{ backgroundColor: i % 2 ? "#EFF3FB" : "#fff" }}>
                              <td style={tdStyle}>{d.parent_code}</td>
                              <td style={tdStyle}>{d.dep_code}</td>
                              <td style={{ ...tdStyle, color: "red" }}>{d.remarks}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: 10, textAlign: "center", borderTop: "1px solid #ccc" }}>
              <button style={okBtnStyle} onClick={() => setValidateOpen(false)}>Close</button>
            </div>
          </div>
        </>
      )}

      <div ref={outerRef} style={{ fontFamily: "Tahoma, Arial, sans-serif", fontSize: 11 }}>

        <div style={{ margin: "0 0 4px 0", display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
          <span style={gridTitleStyle}>DIC File validation list</span>
          <button
            style={exportBtnStyle}
            onClick={() => exportTSV(sortedDicRows, dicHeaders)}
            title="Export to TSV"
          >Export</button>
        </div>

        <div
          ref={dicDivRef}
          id="dic_divScrollableTable"
          style={{ overflowY: "auto", overflowX: "auto", border: "1px solid #A0A0A0", borderTop: "none" }}
        >
          <table
            id="dic_tbl"
            style={{ ...tableStyle, minWidth: "100%", tableLayout: "auto" }}
            cellPadding={3}
            cellSpacing={0}
          >
            <thead>
              <tr
                id="dic_trh"
                style={{
                  backgroundColor: "#2f6089",
                  color: "#fff",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                {dicHeaders.map((h, i) => (
                  <th
                    key={h}
                    style={{
                      ...thStyle,
                      cursor: h === "#" ? "default" : "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      
                    }}
                    onClick={() => {
                      if (h === "#") return;

                      if (dicSortCol === i) setDicSortAsc((a) => !a);
                      else {
                        setDicSortCol(i);
                        setDicSortAsc(true);
                      }
                    }}
                  >
                    {h}
                    {h !== "#" &&
                      (dicSortCol === i ? (dicSortAsc ? " ▲" : " ▼") : "")}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedDicRows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    style={{ textAlign: "center", padding: 20, color: "#888" }}
                  >
                    No data
                  </td>
                </tr>
              )}

              {sortedDicRows.map((r, idx) => {
                const selected = idx === dicSelIdx;

                const rowBg = selected
                  ? "orange"
                  : idx % 2 === 0
                    ? "#ffffff"
                    : "#f4faff";

                const rowColor = selected ? "#fff" : "#000";

                return (
                  <tr
                    key={idx}
                    id={`dic_tr${idx + 1}`}
                    style={{
                      backgroundColor: rowBg,
                      color: rowColor,
                      cursor: "pointer",
                    }}
                    onClick={() => setDicSelIdx(idx)}
                  >
                    {/* Serial Number */}
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {idx + 1}
                    </td>

                    <td style={tdStyle}>{r.dic_file_name}</td>
                    <td style={tdStyle}>{r.short_desc}</td>
                    <td style={tdStyle}>{r.subject}</td>
                    <td style={tdStyle}>{r.country}</td>

                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {r.avl_in_aus ? "Y" : ""}
                    </td>

                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {r.avl_in_can ? "Y" : ""}
                    </td>

                    <td
                      style={{
                        ...tdStyle,
                        color: r.remarks ? "red" : "inherit",
                      }}
                    >
                      {r.remarks}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <table style={{ width: "100%", marginTop: 8 }} cellPadding={5} cellSpacing={0}>
          <tbody>
            <tr>
              <td style={{ textAlign: "center" }}>
                <button
                  style={validateBtnStyle}
                  onClick={() => window.location.href = "/WEBADMIN/DIC/EL_CheckDIC?dicFileName=" + encodeURIComponent(sortedDicRows[dicSelIdx]?.dic_file_name || "") + "&country=" + encodeURIComponent(sortedDicRows[dicSelIdx]?.country || "")}
                >
                  Validate DIC - Common Folder
                </button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <button
                  style={validateBtnStyle}
                  onClick={() => fn_ValidateDIC("D")}
                >
                  Validate DIC - DIC Folder
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        {lsnRows.length > 0 && (
          <>
            <div style={{ margin: "8px 0 4px 0", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={gridTitleStyle}>Lesson/Test Validation</span>
              <button
                style={exportBtnStyle}
                onClick={() => exportTSV(sortedLsnRows, lsnHeaders)}
                title="Export to TSV"
              >Export</button>
            </div>

            <div
              ref={lsnDivRef}
              id="lsn_divScrollableTable"
              style={{ overflowY: "auto", overflowX: "auto", border: "1px solid #A0A0A0", borderTop: "none" }}
            >
              <table
                id="lsn_tbl"
                style={{ ...tableStyle, minWidth: "100%" }}
                cellPadding={3}
                cellSpacing={0}
              >
                <thead>
                  <tr id="lsn_trh" style={{ backgroundColor: "#2f6089", color: "#fff", position: "sticky", top: 0, zIndex: 2 }}>
                    {lsnHeaders.map((h, i) => (
                      <th
                        key={h}
                        style={{ ...thStyle, cursor: "pointer", userSelect: "none" }}
                        onClick={() => {
                          if (lsnSortCol === i) setLsnSortAsc((a) => !a);
                          else { setLsnSortCol(i); setLsnSortAsc(true); }
                        }}
                      >
                        {h}
                        {lsnSortCol === i ? (lsnSortAsc ? " ▲" : " ▼") : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedLsnRows.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                        No validation issues found
                      </td>
                    </tr>
                  )}
                  {sortedLsnRows.map((r, idx) => {
                    const selected = idx === lsnSelIdx;
                    const rowBg = selected ? "blue"
                      : r.remarks ? "#ffe6e6"
                        : idx % 2 === 0 ? "#fff" : "#EFF3FB";
                    return (
                      <tr
                        key={idx}
                        id={`lsn_tr${idx + 1}`}
                        style={{ backgroundColor: rowBg, color: selected ? "#fff" : "#000", cursor: "pointer" }}
                        onClick={() => setLsnSelIdx(idx)}
                      >
                        <td style={tdStyle}>{r.l_or_t}</td>
                        <td style={tdStyle}>{r.child_code}</td>
                        <td style={tdStyle}>{r.child_short_desc}</td>
                        <td style={tdStyle}>{r.dic_file_name}</td>
                        <td style={{ ...tdStyle, color: "red" }}
                          dangerouslySetInnerHTML={{ __html: r.remarks }}
                        />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const ValidateGrid: React.FC<{ rows: ValidateLesson[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return <p style={{ color: "#888", fontSize: 12 }}>None found.</p>;
  }
  return (
    <table style={{ ...tableStyle, width: "100%" }} cellPadding={3} cellSpacing={0}>
      <thead>
        <tr style={{ backgroundColor: "#2f6089", color: "#fff" }}>
          {["Code", "Description", "Level", "Exists", "Remarks"].map((h) => (
            <th key={h} style={thStyle}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={i}
            style={{ backgroundColor: !r.exists ? "#ffe6e6" : i % 2 ? "#EFF3FB" : "#fff" }}
          >
            <td style={tdStyle}>{r.child_code}</td>
            <td style={tdStyle}>{r.description}</td>
            <td style={tdStyle}>{r.level_no}</td>
            <td style={{ ...tdStyle, textAlign: "center", color: r.exists ? "green" : "red" }}>
              {r.exists ? "✓" : "✗"}
            </td>
            <td style={{ ...tdStyle, color: r.remarks ? "red" : "inherit" }}>{r.remarks}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0,
  backgroundColor: "#cccccc",
  opacity: 0.6, zIndex: 99999,
};

const alertBoxStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%", left: "50%",
  transform: "translate(-50%,-50%)",
  width: 400, minHeight: 160,
  backgroundColor: "#fff",
  zIndex: 100000,
  border: "2px solid #CC0000",
  fontFamily: "Tahoma, Arial",
  fontSize: 13, color: "#525252",
  display: "flex", flexDirection: "column",
};

const validateModalStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%", left: "50%",
  transform: "translate(-50%,-50%)",
  width: 1024, height: 768,
  backgroundColor: "#fff",
  zIndex: 100000,
  border: "2px solid #2f6089",
  fontFamily: "Tahoma, Arial", fontSize: 11,
  display: "flex", flexDirection: "column",
  overflow: "hidden",
};

const modalTitleStyle: React.CSSProperties = {
  backgroundColor: "#c7ddfd",
  color: "#2859ac",
  padding: "6px 12px",
  fontWeight: "bold",
  fontSize: 13,
  flexShrink: 0,
};

const sectionTitleStyle: React.CSSProperties = {
  margin: "8px 0 4px",
  fontSize: 12,
  color: "#2f6089",
  borderBottom: "1px solid #2f6089",
};

const tableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  fontFamily: "Trebuchet MS, Arial",
  fontSize: "8pt",
};

const thStyle: React.CSSProperties = {
  padding: "8px 6px",
  textAlign: "left",
  fontWeight: "normal",
  backgroundColor: "#c7ddfd",
  color: "#2859ac",
  border: "1px solid #80A0FF",
  whiteSpace: "nowrap",
  fontSize: "14px"
};

const tdStyle: React.CSSProperties = {
  padding: "8px 6px",
  borderRight: "1px solid #80A0FF",
  fontSize: "14px",
};

const gridTitleStyle: React.CSSProperties = {
  fontFamily: "Trebuchet MS, Arial",
  fontSize: "9pt",
  fontWeight: "bold",
  backgroundColor: "#2f6089",
  color: "#fff",
  padding: "4px 10px",
  display: "inline-block",
};

const exportBtnStyle: React.CSSProperties = {
  fontSize: "8pt",
  fontFamily: "Trebuchet MS, Arial",
  padding: "2px 10px",
  cursor: "pointer",
  border: "1px solid #888",
  background: "#eee",
};

const validateBtnStyle: React.CSSProperties = {
  backgroundColor: "#119ECC",
  color: "#fff",
  border: "none",
  padding: "6px 16px",
  fontSize: "9pt",
  fontFamily: "Trebuchet MS, Arial",
  cursor: "pointer",
  height: 28,
};

const okBtnStyle: React.CSSProperties = {
  backgroundColor: "#2f6089",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "6px 28px",
  fontSize: 12,
  fontFamily: "Tahoma, Arial",
  cursor: "pointer",
  fontWeight: "bold",
};

export default EL_CheckData;